import React from 'react';
import { Download, MessageCircle } from 'lucide-react';
import AppDownloadButton from '../components/AppDownloadButton';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { RECRUITER_WHATSAPP_LINK, WORKER_WHATSAPP_LINK } from '../constants/externalLinks';

function DownloadCard({
  eyebrow,
  title,
  body,
  downloadHref,
  whatsappUrl,
  downloadLabel,
  panelClassName,
}: {
  eyebrow: string;
  title: string;
  body: string;
  downloadHref: string;
  whatsappUrl: string;
  downloadLabel: string;
  panelClassName: string;
}) {
  return (
    <section className={`overflow-hidden rounded-[2rem] border bg-white shadow-[0_24px_60px_-32px_rgba(15,23,42,0.22)] ${panelClassName}`}>
      <div className="p-8 lg:p-10">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">{eyebrow}</p>
        <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">{title}</h2>
        <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600">{body}</p>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row">
          <AppDownloadButton
            href={downloadHref}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            <Download size={17} />
            {downloadLabel}
          </AppDownloadButton>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-6 py-3.5 text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100"
          >
            <MessageCircle size={17} />
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}

export default function DownloadPage() {
  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fffaf5_0%,#ffffff_45%,#f8fafc_100%)] text-slate-900">
      <Seo
        title="Download Jobisa Apps"
        description="Download the Jobisa worker or recruiter app, or start a direct WhatsApp chat with the right Jobisa team."
        path="/download"
      />

      <main className="mx-auto max-w-5xl px-6 py-14 lg:px-10 lg:py-20">
        <div className="mb-10 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-orange-600">Download Center</p>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-950 lg:text-5xl">
            Choose the right Jobisa side
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-600">
            Top section is for workers and candidates. Bottom section is for recruiters and agencies.
          </p>
        </div>

        <div className="space-y-8">
          <DownloadCard
            eyebrow="Worker Candidate"
            title="For workers and candidates"
            body="Download the Jobisa worker app to build your profile and apply for jobs, or start directly on WhatsApp if you want a faster chat-based first step."
            downloadHref="/download/worker"
            whatsappUrl={WORKER_WHATSAPP_LINK}
            downloadLabel="Download Worker App"
            panelClassName="border-orange-100"
          />

          <DownloadCard
            eyebrow="Recruiter"
            title="For recruiters and agencies"
            body="Download the Jobisa recruiter app to manage hiring workflows, or open a direct WhatsApp chat for recruiter-side support and onboarding."
            downloadHref="/download/recruiter"
            whatsappUrl={RECRUITER_WHATSAPP_LINK}
            downloadLabel="Download Recruiter App"
            panelClassName="border-blue-100"
          />
        </div>
      </main>

      <Footer />
    </div>
  );
}
